<template>
  <div class="cart animated fadeIn">
    <b-card>
      <div class="d-flex align-items-center mb-3">
        <h1 class="mr-3">Your Cart</h1>
        <span class="font-xl">Items ({{ totalProducts }})</span>
      </div>

      <div v-if="totalProducts">
        <b-card v-for="product in products" :key="product.id">
          <div class="d-block d-sm-flex align-items-center">
            <div class="flex-grow-1">
              <div class="d-block d-sm-flex align-items-center mb-2">
                <h3 class="d-inline mb-0">
                  Nanome
                  {{ product.details.bundleName || product.details.name }}
                  {{ product.bundle > 1 ? " Bundle" : " License" }}
                </h3>
              </div>
              <small class="d-block font-lg">
                {{
                  cart.items[product.id]
                    | pluralize(product.bundle > 1 ? "Bundle" : "License")
                }}
                Billed Annually
                <span v-if="product.bundle > 1" class="text-muted ml-1">
                  ({{
                    (cart.items[product.id] * product.bundle)
                      | pluralize("License")
                  }})
                </span>
              </small>
              <span v-if="!product.tiers" class="font-xl d-block">
                {{ (product.amount / 100) | currency }} /
                {{ product.interval }}
              </span>
            </div>
            <div class="">
              <div class="cart-item__subtotal pb-2 my-2">
                <small class="d-block">Subtotal</small>
                <span class="font-xl font-weight-bold">
                  {{ calcProductTotal(product) | currency }}
                </span>
              </div>
              <div class="d-flex align-items-center">
                <NanTicker
                  :value="cart.items[product.id]"
                  @input="updateCart(product.id, $event)"
                  :min="1"
                  :max="cart.maxItems"
                  class="w-100 mr-2"
                />
                <b-button @click="removeProduct(product)" variant="danger">
                  <span class="screen-reader-text">Remove from cart</span>
                  <i class="fa fa-trash"></i>
                </b-button>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <b-card v-else class="text-center">
        <h2>Your cart is empty</h2>
        <b-link to="/licenses/add" class="btn btn-primary mt-2">
          Add Licenses to your Cart
        </b-link>
      </b-card>

      <div v-if="totalProducts && !isLoading" class="animated fadeIn">
        <div v-show="token">
          <label v-if="paymentMethods.length > 1">
            <span class="screen-reader-text">Payment method</span>
            <b-form-select
              v-model="selectedPayment"
              id="payment-method"
              :plain="false"
              :options="paymentMethods"
            ></b-form-select>
          </label>

          <template v-if="!selectedPayment">
            <checkout
              ref="checkout"
              class="mt-4"
              :hideSubmit="true"
              @validationChange="validCard = $event"
              @success="onCheckedOut"
            ></checkout>
          </template>

          <small v-if="selectedPayment !== defaultPayment" class="ml-2">
            * This card will be set as your default card for future purchases
          </small>
        </div>

        <div class="mt-4 mb-5">
          <div v-if="!hasCoupon" class="text-right">
            <b-btn
              id="plans-coupon-toggle"
              @click="hasCoupon = true"
              variant="link"
              >Have a coupon?</b-btn
            >
          </div>

          <form
            v-else
            @submit.prevent="checkCoupon(couponCode)"
            class="order-form__coupon"
          >
            <label for="coupon-input" class="flex-grow-1"> Coupon </label>
            <div class="d-flex">
              <input
                v-model="couponCode"
                :disabled="!!couponDetails"
                id="coupon-input"
                class="form-control"
              />
              <b-btn
                v-if="!!couponDetails"
                @click="removeCoupon"
                variant="link"
                type="button"
                >Reset</b-btn
              >
              <b-btn v-else variant="link" type="submit"> Apply </b-btn>
            </div>
          </form>

          <p v-if="couponDetails" class="text-success">
            Success: {{ couponDetails }}
          </p>
        </div>

        <b-row cols="1" cols-md="2">
          <b-col class="text-center">
            <h4>Order Summary:</h4>
            <h5>Subtotal: {{ (subtotal / 100) | currency }}</h5>
            <h2>Due Today: {{ (total / 100) | currency }}</h2>
          </b-col>

          <b-col class="text-muted">
            By purchasing online, you are agreeing to enroll in an automatically
            renewing license subscription. You will automatically be charged
            each year during your billing cycle. If you cannot enroll in an
            automatically renewing subscription, please send a request
            <a href="https://nanome.ai/quote" target="_blank">here</a>.
          </b-col>
        </b-row>

        <div class="cart__purchase mt-4 text-center">
          <b-btn
            v-if="selectedPayment"
            @click="makePayment"
            id="plans-pay-submit"
            :class="{ disabled: !subtotal }"
            variant="primary"
          >
            Complete Purchase
          </b-btn>

          <b-btn
            v-else
            @click="getStripeToken"
            id="plans-pay-submit"
            :class="{ disabled: !validCard }"
            variant="primary"
          >
            Complete Purchase
          </b-btn>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import API from "@/api";

import Checkout from "@/components/Checkout";
import products from "@/data/products";
import { calcTotalPrice } from "@/helpers/pricing";

export default {
  components: {
    Checkout
  },

  metaInfo: {
    titleTemplate: "My Cart - %s"
  },

  data: () => ({
    hasCoupon: false,
    couponObj: null,
    couponCode: "",
    couponLoading: false,
    validCard: false,
    defaultPayment: null,
    selectedPayment: null,
    isLoading: true,
    error: null
  }),

  computed: {
    ...mapState(["token", "cart"]),
    ...mapState("billing", ["methods"]),
    ...mapGetters("cart", ["totalProducts", "subtotal", "products"]),

    total() {
      if (!this.couponObj) return this.subtotal;

      let amountOff;
      if (this.couponObj.amount_off) {
        amountOff = this.couponObj.amount_off;
      } else if (this.couponObj.percent_off) {
        amountOff = this.subtotal * (this.couponObj.percent_off / 100);
      }
      return Math.max(0, this.subtotal - amountOff);
    },

    couponDetails() {
      if (!this.couponObj || !this.couponObj.valid) {
        return "";
      }

      const coupon = this.couponObj;
      let amountOff = "";
      let duration;

      if (coupon.amount_off) {
        if (coupon.currency === "usd") {
          amountOff = `$${coupon.amount_off / 100}`;
        } else {
          amountOff = `${coupon.amount_off / 100} ${coupon.currency}`;
        }
      } else if (coupon.percent_off) {
        amountOff = `${coupon.percent_off}%`;
      }

      if (coupon.duration === "forever") {
        duration = "";
      } else if (coupon.duration === "repeating") {
        duration = ` for ${coupon.duration_in_months} month/s`;
      } else if (coupon.duration === "once") {
        duration = ` for the first billing cycle`;
      }

      return `${amountOff} off` + `${duration}.`;
    },

    paymentMethods() {
      const paymentMethods = [];

      if (this.methods) {
        this.methods.list.forEach(card => {
          let text = `Use ${card.brand} ending in ${card.last4}`;
          if (card.id === this.defaultPayment) text += " (default)";
          paymentMethods.push({ value: card.id, text });
        });
      }

      paymentMethods.push({
        value: null,
        text: "Use a different card"
      });

      return paymentMethods;
    }
  },

  watch: {
    "token.value": {
      async handler(tokenId) {
        if (!tokenId) {
          this.defaultPayment = null;
          return;
        }

        await this.$store.dispatch("billing/methods");
        if (this.methods.current) {
          this.defaultPayment = this.methods.current.id;
          this.selectedPayment = this.methods.current.id;
        }
      },
      immediate: true
    }
  },

  async created() {
    this.getLicensesFromRoute();
    this.isLoading = false;
  },

  methods: {
    calcProductTotal(product) {
      const quantity = this.cart.items[product.id];
      const total = calcTotalPrice(product, quantity);
      return total / 100;
    },

    updateCart(id, count) {
      this.$store.dispatch("cart/setProductCount", { id, count });
    },

    getLicensesFromRoute() {
      const shortIds = Object.keys(products.available);
      const productIds = Object.values(products.available);
      const ids = [...shortIds, ...productIds];

      ids.forEach(id => {
        const count = +this.$route.query[id];
        if (count && count > 0) {
          this.$store.dispatch("cart/setProductCount", {
            id: shortIds.includes(id) ? products.available[id] : id,
            count
          });
        }
      });
    },

    async removeProduct(product) {
      let name = product.details.bundleName || product.details.name;
      name += product.bundle > 1 ? " Bundle" : " License";

      const confirm = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to remove Nanome ${name} from your cart?`,
        {
          title: "Remove From Cart",
          okTitle: "Yes",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );

      if (!confirm) {
        return;
      }

      this.$store.dispatch("cart/setProductCount", {
        id: product.id,
        count: 0
      });
    },

    async checkCoupon(coupon) {
      this.couponLoading = true;
      this.couponObj = null;
      if (!coupon) return;

      try {
        const { success, results } = await API.lookupCoupon(coupon);
        if (success) {
          this.couponObj = results;
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.couponLoading = false;
      }
    },

    removeCoupon() {
      this.couponObj = null;
      this.couponCode = "";
    },

    async getStripeToken() {
      if (!this.validCard) {
        this.$refs.checkout.$v.$touch();
        return;
      }
      await this.$refs.checkout.onSubmit();
    },

    async onCheckedOut({ tokenId, cardInfo }) {
      this.$store.commit("REQUEST_INCREMENT");
      await API.addPaymentMethod(tokenId);

      await API.selectPaymentMethod(cardInfo.id);
      this.defaultPayment = null;

      await this.makePayment();
      this.$store.commit("REQUEST_DECREMENT");
    },

    async makePayment() {
      if (!this.subtotal) {
        this.$refs.checkout.$v.$touch();
        return;
      }

      let success = false;

      try {
        this.$store.commit("REQUEST_INCREMENT");

        if (this.selectedPayment !== this.defaultPayment) {
          await API.selectPaymentMethod(this.selectedPayment);
        }

        // Filter just the selected products and transform to the API calls for each one
        const requests = this.products
          .filter(product => this.cart.items[product.id] > 0)
          .map(product => {
            const quantity = this.cart.items[product.id];
            const productTotal = calcTotalPrice(product, quantity);
            const total = (productTotal / this.subtotal) * this.total;

            return API.purchase({
              plan: product.id,
              quantity,
              coupon: this.couponCode,
              total
            });
          });

        const responses = await Promise.all(requests);

        if (responses[0].success) {
          success = true;
        }
      } catch (err) {
        console.warn(err);
        this.$notify({
          type: "error",
          title: "Error:",
          text: "There was an error with the request."
        });
      } finally {
        this.$store.commit("REQUEST_DECREMENT");

        if (success) {
          this.$store.dispatch("cart/clearCart");

          const answer = await this.$bvModal.msgBoxConfirm(
            "Thanks for purchasing licenses for Nanome. Now continue to the licenses page to manage your new licenses!",
            {
              title: "Thank You!",
              okTitle: "Manage My Licenses",
              okVariant: "primary",
              cancelTitle: "No Thanks"
            }
          );

          if (answer) {
            this.$router.push({ path: "/licenses" });
          }
        }
      }
    }
  }
};
</script>

<style lang="scss">
.cart {
  & &-item {
    &__subtotal {
      @include breakpoint(sm) {
        text-align: right;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
    }
  }

  & &__purchase {
    @include breakpoint(sm) {
      text-align: right !important;
    }
  }
}
</style>
